import { useEffect, useState } from 'react'
import { counts, onOutboxChange, runSync } from '../lib/sync'
import { t } from '../lib/i18n'

/**
 * THE BANNER THAT TELLS THE TRUTH
 * -------------------------------
 * "Saved" and "the office can see it" are different facts, and an officer is
 * entitled to know which one is true. An app that says only "Saved" while
 * twelve households sit on the phone is not being reassuring — it is being
 * misleading, and the moment that phone is lost or reset, nobody knew.
 *
 * So the state is always visible and always plain:
 *
 *   nothing waiting   a quiet line, or nothing at all
 *   waiting           "3 waiting to send" — normal, not an error. In a village
 *                     this is what the whole day looks like and it must not
 *                     read as a warning.
 *   needs attention   something the server refused. This IS a problem and a
 *                     supervisor has to see it.
 */
export function SyncBar() {
  const [pending, setPending] = useState(0)
  const [rejected, setRejected] = useState(0)
  const [online, setOnline] = useState(navigator.onLine)
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    const read = async () => {
      const c = await counts()
      if (!alive) return
      setPending(c.pending)
      setRejected(c.rejected)
    }
    read()

    const off = onOutboxChange(read)
    const on = () => setOnline(true)
    const gone = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', gone)

    return () => {
      alive = false
      off()
      window.removeEventListener('online', on)
      window.removeEventListener('offline', gone)
    }
  }, [])

  async function send(includeRejected = false) {
    setBusy(true)
    setSaid(null)
    const r = await runSync({ includeRejected })
    setBusy(false)

    if (r.sent > 0 && r.remaining === 0) setSaid(`${t('sync.send')} ${r.sent}. ${t('sync.allSent')}`)
    else if (r.stopped === 'offline')
      setSaid(t('sync.stillOffline'))
    else if (r.stopped === 'signed_out') setSaid(t('sync.signInAgain'))
    else if (r.rejected > 0) setSaid(t('sync.someRefused'))
    else if (r.sent > 0) setSaid(`${t('sync.send')} ${r.sent}.`)

    const c = await counts()
    setPending(c.pending)
    setRejected(c.rejected)
  }

  if (pending === 0 && rejected === 0) {
    // Offline with an empty queue is still worth saying — an officer who does
    // not know they are offline cannot decide to walk up the hill.
    if (online) return null
    return (
      <div className="syncbar syncbar-quiet">
        {t('sync.offlineSafe')}
      </div>
    )
  }

  return (
    <div className={rejected > 0 ? 'syncbar syncbar-warn' : 'syncbar'}>
      <div style={{ flex: 1 }}>
        {pending > 0 && (
          <strong>
            {pending} {t('sync.waiting')}
          </strong>
        )}
        {pending > 0 && rejected > 0 && ' · '}
        {rejected > 0 && <strong>{rejected} {t('sync.needAttention')}</strong>}
        <div style={{ fontSize: 14, opacity: 0.9 }}>
          {said ??
            (online
              ? t('sync.savedTapToSend')
              : t('sync.willSendItself'))}
        </div>
      </div>

      <button
        onClick={() => send(rejected > 0 && pending === 0)}
        disabled={busy}
        style={{ minWidth: 96, marginLeft: 10 }}
      >
        {busy ? t('sync.sending') : rejected > 0 && pending === 0 ? t('sync.tryAgain') : t('sync.send')}
      </button>
    </div>
  )
}
